import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SymbolV2 } from "@bn/api/apiv1";
import { SearchModal } from "@components/Modal/SearchModal";
import SearchIcon from "../../../../icons/myicons/sidebar/search.svg?react";

const Container = styled.div<{
  collapsed: number;
  sideBar: boolean;
  bgColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: ${({ theme }) => theme.spacing(1.5, 3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  cursor: pointer;
  margin-top: ${({ theme, collapsed }) => (collapsed ? theme.spacing(4) : 0)};
  color: ${({ theme }) => theme.palette.sidebar.searchText};
  background-color: ${({ theme, bgColor }) =>
    bgColor ? bgColor : theme.palette.sidebar.searchBg};
  border: 1px solid
    ${({ theme, sideBar }) =>
      sideBar
        ? theme.palette.sidebar.searchBorder
        : theme.palette.border.primary};
  box-shadow: ${({ theme }) =>
    theme.type === "ftd" ? theme.shadows[1] : "none"};
`;

const SmallSearchButton = styled(Box)`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.sidebar.searchText};

  &:hover {
    color: ${({ theme }) => theme.palette.text.onColorDark};
    background-color: ${({ theme }) => theme.palette.sidebar.hover};
  }
`;

interface FakeSearchFieldProps {
  collapsed: boolean;
  placeholder?: string;
  bgColor?: string;
  sideBar?: boolean;
  onSymbolSelect?: (symbol: SymbolV2) => void;
  isSymbolClickable?: (symbol: SymbolV2) => boolean;
  getSymbolHref?: (symbol: SymbolV2) => string | undefined;
}

export const FakeSearchField = (props: FakeSearchFieldProps) => {
  const {
    collapsed,
    bgColor,
    onSymbolSelect,
    isSymbolClickable,
    getSymbolHref,
    sideBar = false,
    placeholder = "appSidebar.searchField",
  } = props;
  const theme = useTheme();

  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target as HTMLElement).closest(".clear")) {
      setOpen(true);
    }
  };

  const handleSymbolSelect = (symbol: SymbolV2) => {
    setOpen(false);
    onSymbolSelect?.(symbol);
  };

  if (collapsed && !matches) {
    return (
      <>
        <SmallSearchButton onClick={handleClick}>
          <SearchIcon />
        </SmallSearchButton>
        <SearchModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSymbolSelect={handleSymbolSelect}
          isSymbolClickable={isSymbolClickable}
          getSymbolHref={getSymbolHref}
        />
      </>
    );
  }

  return (
    <>
      <Container
        onClick={handleClick}
        collapsed={collapsed ? 1 : 0}
        bgColor={bgColor}
        sideBar={sideBar}
      >
        <SearchIcon color={theme.palette.sidebar.searchText} />
        <Typography
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
          variant="body3"
          color={
            sideBar
              ? theme.palette.sidebar.searchText
              : theme.palette.text.tertiary
          }
        >
          {t(placeholder)}
        </Typography>
      </Container>
      {open && (
        <SearchModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSymbolSelect={handleSymbolSelect}
          isSymbolClickable={isSymbolClickable}
          getSymbolHref={getSymbolHref}
        />
      )}
    </>
  );
};
